import { IconButton } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddLine,
  CreateStackLine,
  LinkLine,
  MinusLine,
  MoreHorizontalLine,
} from "@dropbox/dig-icons/assets";
import { useManuallyAddedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { ResultAction } from "@mirage/mosaics/Results/ResultActions";
import { DisplayActionTypes } from "@mirage/search/SearchResults/ResultRow/types";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { copyToClipboard } from "@mirage/service-platform-actions";
import { showSnackbar } from "@mirage/shared/snackbar";
import { useShowStackChooserModal } from "@mirage/stacks/StackChooser/StackChooser";
import i18n from "@mirage/translations";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChatSource } from "../../types";

export const SourceActionBar = ({
  source,
  title,
  url,
}: {
  source: ChatSource;
  title: string;
  url: string;
}) => {
  const { addSource, removeSource, isSourceAdded } = useManuallyAddedSources();

  const sourceIsAdded = isSourceAdded(source);

  return (
    <Box>
      <ResultAction
        action={{
          key: sourceIsAdded ? "remove_from_sources" : "add_to_sources",
          title: sourceIsAdded
            ? i18n.t("remove_source_for_chat")
            : i18n.t("add_to_sources_for_chat"),
          type: DisplayActionTypes.IconButton,
          icon: <UIIcon src={sourceIsAdded ? MinusLine : AddLine} />,
          onClick: () =>
            sourceIsAdded ? removeSource(source) : addSource(source),
        }}
      />
      <SourceOverflowMenuAction url={url} title={title} />
    </Box>
  );
};

enum SourceOverflowAction {
  COPY_LINK = "copy_link",
  ADD_TO_STACK = "add_to_stack",
}

const SourceOverflowMenuAction = ({
  url,
  title,
}: {
  url: string;
  title: string;
}) => {
  const navigate = useNavigate();

  const showStackChooserModal = useShowStackChooserModal(navigate);

  const copyLink = useCallback(() => {
    // TODO (matto): Report PAP event
    copyToClipboard(url);
    showSnackbar({ title: i18n.t("copied_link_to_clipboard") });
  }, [url]);

  const addToStack = useCallback(() => {
    // TODO (matto): Report PAP event
    showStackChooserModal({
      searchTerm: title,
      metadataTitle: title,
      url: url,
      callback: () => {
        // TODO: pap event?
        publishEvent(SurveyEvent.AddStackItem, {}, true);
      },
    });
  }, [title, url, showStackChooserModal]);

  const onSelection = useCallback(
    (value: SourceOverflowAction) => {
      // TODO (matto): Report PAP event

      switch (value) {
        case SourceOverflowAction.COPY_LINK:
          copyLink();
          break;
        case SourceOverflowAction.ADD_TO_STACK:
          addToStack();
          break;
      }
    },
    [copyLink, addToStack],
  );

  return (
    <Menu.Wrapper
      shouldPropagateClickOutsideMouseEvents
      closeOnSelection={true}
      onSelection={onSelection}
      onToggle={({ isOpen }) => {
        if (isOpen) {
          // TODO (matto): Report PAP event
        }
      }}
    >
      {({ getContentProps, getTriggerProps }) => (
        <>
          <IconButton
            {...getTriggerProps()}
            tone="neutral"
            variant="borderless"
            aria-label={i18n.t("more")}
          >
            <Box color="Text Subtle" as={UIIcon} src={MoreHorizontalLine} />
          </IconButton>
          <Menu.Content {...getContentProps()}>
            <Menu.Segment>
              <Menu.ActionItem
                value={SourceOverflowAction.COPY_LINK}
                withTitle={i18n.t("copy_link")}
                withLeftAccessory={<UIIcon src={LinkLine} />}
              />
              <Menu.ActionItem
                value={SourceOverflowAction.ADD_TO_STACK}
                withTitle={i18n.t("add_to_stack")}
                withLeftAccessory={<UIIcon src={CreateStackLine} />}
                onClick={addToStack}
              />
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
